'use client';

import { forwardRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { Play2Icon } from '@paid-ui/icons/play-2';
import { appConfigManager } from '@paid-ui/models/app-config';
import { requiringActionManager } from '@paid-ui/models/requiring-action';
import { useGetProjects } from '@paid-ui/queries/search-projects';

import { NewProjectButton } from '../new-project-button';
import { ProjectCard } from '../project-card';
import { ProjectFilters } from '../project-filters';
import { CardsPagination } from './CardsPagination';
import { CardsToolbar } from './CardsToolbar';
import { useApplyFilters } from './use-apply-filter';

interface Props extends React.HTMLAttributes<HTMLDivElement> {}

/** A multiple of the three-column grid, so the last row is never a stub. */
const defaultPageSize = 12;

const filterParams = [
  'searchTerm',
  'projectStatus',
  'sortByKey',
  'sortByDirection',
  'page',
  'size',
];

export const ProjectCards = forwardRef<HTMLDivElement, Props>(
  ({ hidden, className, ...restProps }, ref) => {
    const { externalLinks } = useSnapshot(appConfigManager);
    const { numberOfProjectActions } = useSnapshot(requiringActionManager);
    const searchParams = useSearchParams();
    const applyFilters = useApplyFilters();

    const {
      data: result,
      isLoading,
      isFetching,
      isError,
      refetch,
    } = useGetProjects({ defaultSize: defaultPageSize });

    if (hidden) {
      return null;
    }

    const projects = result?.data ?? [];
    const total = result?.total ?? 0;
    const totalPages = result?.totalPages ?? 0;
    const currentPage = result?.currentPage ?? 0;
    const pageSize = Number.parseInt(searchParams.get('size') || String(defaultPageSize));

    const isFiltered = filterParams.some((param) => searchParams.get(param));
    const searchTerm = searchParams.get('searchTerm');

    const clearFilters = () => {
      applyFilters(Object.fromEntries(filterParams.map((param) => [param, ''])));
    };

    return (
      <div ref={ref} className={twMerge('space-y-6', className)} {...restProps}>
        <header className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2.5 gap-y-4">
          <h2 className="col-span-2 flex items-center gap-x-2 text-2xl font-semibold lg:col-span-1">
            <span>My projects</span>
            <a
              href={externalLinks.runningProjectsOnPaidVideo}
              target="_blank"
              rel="noreferrer noopener"
              title="Click to watch the video"
              className="mt-1 shrink-0 text-blue transition-colors hover:text-blue-dark"
            >
              <Play2Icon className="h-6 w-6" />
            </a>
          </h2>
          {/* The empty-state card carries its own button, so don't offer two. */}
          <NewProjectButton
            hidden={!isLoading && !isFiltered && projects.length === 0}
            className="order-2 lg:order-3"
          />
          <ProjectFilters
            disabled={isFetching}
            className="order-3 col-span-3 lg:order-2 lg:col-span-1"
          />
        </header>

        <CardsToolbar disabled={isFetching} total={isLoading ? undefined : total} />

        {isError ? (
          <div className="grid justify-items-center gap-y-3 rounded border border-grey-mid bg-white p-10">
            <p className="text-base font-medium">We could not load your projects.</p>
            <p className="text-sm text-grey-dark">Check your connection and try again.</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        ) : (
          <div
            className={twMerge(
              'grid grid-cols-1 gap-5 transition-opacity duration-150 ease-linear sm:grid-cols-2 xl:grid-cols-3',
              // Paging and sorting keep the previous page on screen; dim it rather than
              // swapping in skeletons, which makes the grid flash on every click.
              isFetching && !isLoading ? 'opacity-60' : '',
            )}
          >
            {isLoading ? (
              Array.from({ length: defaultPageSize }, (_, index) => (
                <ProjectCard key={index} loading />
              ))
            ) : projects.length > 0 ? (
              projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  data={project}
                  badge={numberOfProjectActions[project.id]}
                />
              ))
            ) : isFiltered ? (
              <div className="col-span-full grid justify-items-center gap-y-3 rounded border border-grey-mid bg-white p-10">
                <p className="text-base font-medium">
                  {searchTerm
                    ? `No projects match “${searchTerm}”.`
                    : 'No projects match those filters.'}
                </p>
                <p className="text-sm text-grey-dark">
                  Try a different project number, name or address.
                </p>
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              </div>
            ) : (
              <ProjectCard />
            )}
          </div>
        )}

        <CardsPagination
          hidden={isError || isLoading || totalPages <= 1}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          defaultPageSize={defaultPageSize}
          disabled={isFetching}
        />
      </div>
    );
  },
);

ProjectCards.displayName = 'ProjectCards';
